/**
 * The fixture behind `/__ui/admin?screen=connections` — no session, no api, no database, so a
 * served build can be hit-tested by `tools/browser/verify-automations-local.mjs`.
 *
 * The shape mirrors the state a fresh install is in: Slack connected, everything else not.
 */
import type { ConnectionRow, ConnectionsSource, SaveConnectionArgs } from './types';

const now = new Date('2026-09-11T09:00:00.000Z').toISOString();

export const FIXTURE_CONNECTIONS: ConnectionRow[] = [
  {
    id: '1000000000001',
    app: 'slack',
    label: 'Eli Z Group workspace',
    status: 'connected',
    hasSecret: true,
    secretHint: '4f2a',
    expiresAt: null,
    metadata: { team: 'Eli Z Group', automationCount: 8 },
    lastUsedAt: now,
    lastErrorAt: null,
    lastError: null,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: '1000000000002',
    app: 'webhooks',
    label: 'Outgoing webhooks',
    status: 'connected',
    hasSecret: false,
    secretHint: null,
    expiresAt: null,
    metadata: { allowedHosts: ['hooks.example.com'], automationCount: 42 },
    lastUsedAt: null,
    lastErrorAt: null,
    lastError: null,
    createdAt: now,
    updatedAt: now,
  },
];

export function createFixtureConnections(): ConnectionsSource {
  let rows = FIXTURE_CONNECTIONS.map((row) => ({ ...row }));
  return {
    list: async () => rows.map((row) => ({ ...row })),
    save: async (input: SaveConnectionArgs) => {
      const existing = rows.find((row) => row.app === input.app);
      const next: ConnectionRow = {
        ...(existing ?? {
          id: `10000000000${String(rows.length + 3)}`,
          app: input.app,
          hasSecret: false,
          secretHint: null,
          expiresAt: null,
          lastUsedAt: null,
          lastErrorAt: null,
          lastError: null,
          createdAt: now,
          metadata: {},
          label: null,
          status: 'not_connected' as const,
          updatedAt: now,
        }),
        label: input.label ?? null,
        metadata: input.metadata ?? {},
        status: input.app === 'webhooks' || input.secret !== undefined ? ('connected' as const) : ('not_connected' as const),
        hasSecret: input.secret !== undefined,
        secretHint: input.secret === undefined ? (existing?.secretHint ?? null) : input.secret.slice(-4),
        updatedAt: new Date().toISOString(),
      };
      rows = [...rows.filter((row) => row.app !== input.app), next];
      return next;
    },
    disconnect: async ({ app }) => {
      const had = rows.some((row) => row.app === app);
      rows = rows.filter((row) => row.app !== app);
      return { app, deleted: had };
    },
  };
}
